import React, {useState, useEffect, useRef} from 'react';
import {View, FlatList, StyleSheet, ScrollView, Pressable} from 'react-native';
import SlideModal from '@/Atoms/SlideModal';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCColors from '@/Utils/CCColors';
import {FontSizes, vs} from '@/Utils/Dimensions';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import Spacer from '@/Atoms/Spacer';
import dayjs from 'dayjs';
import {getUTCOffset} from '@/Utils/CommonUtils';
// Reuse RescheduleModal's slot selection logic
import RescheduleModal, {AvailableSlots} from '@/Screens/Books/components/RescheduleModel';
import { useMediaQuery } from '@/Hooks/useMediaQuery/useMediaQuery';

export interface SelectedSlot {
  date: string;
  time: string;
}

interface DateTimeSlotModalProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (slots: SelectedSlot[]) => void;
  selectedSlots?: SelectedSlot[];
  allowMultiple?: boolean;
  isCustomTiming?: boolean;
  availableSlots?: {[date: string]: Array<{start_time: string; end_time: string}>};
}

const DateTimeSlotModal: React.FC<DateTimeSlotModalProps> = ({
  visible,
  onClose,
  onSelect,
  selectedSlots: initialSelectedSlots = [],
  allowMultiple = true,
  isCustomTiming = false,
  availableSlots,
}) => {
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedSlots, setSelectedSlots] = useState<SelectedSlot[]>(initialSelectedSlots);
  const containerRef = useRef<any>(null);
  const {isMobileOrTab} = useMediaQuery();
  // Generate dates for the next 7 days
  const generateDates = (): Array<{dateStr: string; displayDate: string; dayName: string}> => {
    const dates: Array<{dateStr: string; displayDate: string; dayName: string}> = [];
    for (let i = 0; i < 7; i++) {
      const date = dayjs().add(i, 'day');
      dates.push({
        dateStr: date.format('YYYY-MM-DD'),
        displayDate: date.format('MMM D'),
        dayName: date.format('ddd'),
      });
    }
    return dates;
  };

  const dates = generateDates();

  // Generate time slots (all 24 hours) - compact format
  const generateTimeSlots = (): Array<{start_time: string; displayTime: string; isAvailable: boolean}> => {
    const slots: Array<{start_time: string; displayTime: string; isAvailable: boolean}> = [];
    // Generate slots for all 24 hours (12 AM to 11 PM)
    for (let hour = 0; hour < 24; hour++) {
      const time = dayjs().hour(hour).minute(0).second(0);
      slots.push({
        start_time: time.format('HH:mm:ss'),
        displayTime: time.format('h:mm A'),
        isAvailable: true,
      });
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  useEffect(() => {
    if (visible && dates.length > 0 && !selectedDate) {
      setSelectedDate(dates[0].dateStr);
    }
  }, [visible, dates]);

  useEffect(() => {
    if (initialSelectedSlots && initialSelectedSlots.length > 0) {
      setSelectedSlots(initialSelectedSlots);
      // Set selected date to first slot's date if available
      if (initialSelectedSlots[0].date) {
        setSelectedDate(initialSelectedSlots[0].date);
      }
    }
  }, [initialSelectedSlots]);

  useEffect(() => {
    if (!visible) {
      setSelectedDate('');
      // Don't clear selectedSlots on close - keep them for next open
    }
  }, [visible]);

  const handleDateSelect = (dateStr: string) => {
    setSelectedDate(dateStr);
  };

  const handleTimeSelect = (time: string) => {
    if (!selectedDate) return;
    
    const slotKey = `${selectedDate}_${time}`;
    const slot: SelectedSlot = { date: selectedDate, time };
    
    if (allowMultiple) {
      // Toggle selection - add if not exists, remove if exists
      const existingIndex = selectedSlots.findIndex(
        s => s.date === selectedDate && s.time === time
      );
      
      if (existingIndex >= 0) {
        // Remove slot
        setSelectedSlots(selectedSlots.filter((_, index) => index !== existingIndex));
      } else {
        // Add slot
        setSelectedSlots([...selectedSlots, slot]);
      }
    } else {
      // Single selection mode
      setSelectedSlots([slot]);
    }
  };

  const handleRemoveSlot = (index: number) => {
    setSelectedSlots(selectedSlots.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (selectedSlots.length > 0) {
      onSelect(selectedSlots);
    }
  };

  const isSlotSelected = (date: string, time: string): boolean => {
    return selectedSlots.some(slot => slot.date === date && slot.time === time);
  };

  const handleDidntFindSlot = () => {
    // Handle "Didn't Find a Slot?" action
    console.log('Didn\'t find slot clicked');
  };

  // Count available slots for selected date (all slots are available)
  const availableSlotsCount = timeSlots.length;

  return (
    <SlideModal
      visible={visible}
      showHeader
      showCloseIcon
      onClose={onClose}
      type="center"
      customStyle={{
        minWidth: isMobileOrTab ? '80vw' : '500px',
        maxWidth: isMobileOrTab ? '90vw' : '70vw',
        // width: 'auto',
        minHeight: 500,
      }}
      modalHeaderTitle={
        <Box type="row-center" gap={3}>
          <MaterialCommunityIcons name="calendar-outline" size={18} color={CCColors.TextColor.Dark} />
          <CCText fontSize={FontSizes[16]} fontWeight="semibold">
            {isCustomTiming ? 'Select custom timing' : 'Select date and time slot'}
          </CCText>
        </Box>
      }>
      <Box padding={20}>
        {/* Date Selection */}
        <Box marginBottom={20}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <Box flexDirection="row" gap={10}>
              {dates.map(date => {
                const isSelected = selectedDate === date.dateStr;
                return (
                  <Pressable
                    key={date.dateStr}
                    onPress={() => handleDateSelect(date.dateStr)}
                    style={[
                      styles.dateCard,
                      isSelected && styles.dateCardSelected,
                    ]}>
                    <CCText
                      fontSize={FontSizes[14]}
                      color={isSelected ? CCColors.White : CCColors.TextColor.Dark}
                      fontWeight={isSelected ? 'bold' : 'semibold'}>
                      {date.dayName}
                    </CCText>
                  </Pressable>
                );
              })}
            </Box>
          </ScrollView>
        </Box>

        {/* Availability Info */}
        <Box flexDirection="row" justifyContent="space-between" alignItems="center" marginBottom={15}>
          <Box flexDirection="row" alignItems="center" gap={5}>
            <Box width={8} height={8} borderRadius={4} backgroundColor={CCColors.Green} />
            <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Dark}>
              {availableSlotsCount} Slots available
            </CCText>
            {allowMultiple && selectedSlots.length > 0 && (
              <CCText fontSize={FontSizes[12]} color={CCColors.Primary.Default} fontWeight="semibold">
                ({selectedSlots.length} selected)
              </CCText>
            )}
          </Box>
          <Pressable onPress={handleDidntFindSlot}>
            <CCText fontSize={FontSizes[12]} color={CCColors.BlueAqua}>
              Didn't Find a Slot?
            </CCText>
          </Pressable>
        </Box>

        {/* Selected Slots Display */}
        {allowMultiple && selectedSlots.length > 0 && (
          <Box marginBottom={15} padding={12} backgroundColor={CCColors.Grey.Divider200} borderRadius={8}>
            <CCText fontSize={FontSizes[12]} fontWeight="semibold" marginBottom={8} color={CCColors.TextColor.Dark}>
              Selected Slots ({selectedSlots.length}):
            </CCText>
            <Box flexDirection="row" flexWrap="wrap" gap={8}>
              {selectedSlots.map((slot, index) => {
                const slotDate = dayjs(slot.date);
                const dayName = slotDate.format('ddd'); // e.g., "Mon", "Tue"
                return (
                  <Box
                    key={`${slot.date}_${slot.time}_${index}`}
                    flexDirection="row"
                    alignItems="center"
                    gap={4}
                    backgroundColor={CCColors.White}
                    paddingHorizontal={8}
                    paddingVertical={5}
                    borderRadius={6}
                    borderWidth={1}
                    borderColor={CCColors.Primary.Default}>
                    <CCText 
                      fontSize={FontSizes[10]} 
                      color={CCColors.TextColor.Dark}
                      numberOfLines={1}
                      style={{maxWidth: 75, flexShrink: 1}}>
                      {dayName} {(() => {
                        // Parse time - could be HH:mm:ss or full datetime
                        try {
                          const timeStr = slot.time.includes('T') 
                            ? dayjs(slot.time).format('h:mm A')
                            : dayjs(`2000-01-01 ${slot.time}`).format('h:mm A');
                          return timeStr;
                        } catch {
                          return slot.time;
                        }
                      })()}
                    </CCText>
                    <Pressable onPress={() => handleRemoveSlot(index)}>
                      <MaterialCommunityIcons name="close-circle" size={14} color={CCColors.Red} />
                    </Pressable>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Time Slot Selection */}
        <Box marginBottom={20}>
          <ScrollView style={{maxHeight: 350}} showsVerticalScrollIndicator={true}>
            {/* AM Slots */}
            <Box marginBottom={15}>
              <CCText fontSize={FontSizes[12]} fontWeight="semibold" color={CCColors.TextColor.Grey} marginBottom={8}>
                AM
              </CCText>
              <Box flexDirection="row" flexWrap="wrap" gap={6}>
                {timeSlots.filter(slot => slot.displayTime.includes('AM')).map((slot, index) => {
                  const isSelected = isSlotSelected(selectedDate, slot.start_time);
                  return (
                    <Pressable
                      key={`am-${index}`}
                      onPress={() => handleTimeSelect(slot.start_time)}
                      style={[
                        styles.timeSlot,
                        isSelected && styles.timeSlotSelected,
                      ]}>
                      {isSelected && (
                        <MaterialCommunityIcons name="check" size={10} color={CCColors.White} style={{marginRight: 2}} />
                      )}
                      <CCText
                        fontSize={FontSizes[10]}
                        color={isSelected ? CCColors.White : CCColors.TextColor.Dark}
                        fontWeight={isSelected ? 'semibold' : 'regular'}>
                        {slot.displayTime}
                      </CCText>
                    </Pressable>
                  );
                })}
              </Box>
            </Box>

            {/* PM Slots */}
            <Box>
              <CCText fontSize={FontSizes[12]} fontWeight="semibold" color={CCColors.TextColor.Grey} marginBottom={8}>
                PM
              </CCText>
              <Box flexDirection="row" flexWrap="wrap" gap={6}>
                {timeSlots.filter(slot => slot.displayTime.includes('PM')).map((slot, index) => {
                  const isSelected = isSlotSelected(selectedDate, slot.start_time);
                  return (
                    <Pressable
                      key={`pm-${index}`}
                      onPress={() => handleTimeSelect(slot.start_time)}
                      style={[
                        styles.timeSlot,
                        isSelected && styles.timeSlotSelected,
                      ]}>
                      {isSelected && (
                        <MaterialCommunityIcons name="check" size={10} color={CCColors.White} style={{marginRight: 2}} />
                      )}
                      <CCText
                        fontSize={FontSizes[10]}
                        color={isSelected ? CCColors.White : CCColors.TextColor.Dark}
                        fontWeight={isSelected ? 'semibold' : 'regular'}>
                        {slot.displayTime}
                      </CCText>
                    </Pressable>
                  );
                })}
              </Box>
            </Box>
          </ScrollView>
        </Box>

        {/* Timezone Info */}
        <Box marginBottom={20}>
          <CCText fontSize={FontSizes[10]} color={CCColors.TextColor.Grey}>
            (GMT+05:30) India Standard Time - Kolkata
          </CCText>
        </Box>

        {/* Action Buttons */}
        <Box flexDirection="row" gap={10}>
          <CCButton
            onPress={onClose}
            style={[styles.cancelButton]}>
            <CCText fontSize={FontSizes[16]} fontWeight="semibold" >
              Cancel
            </CCText>
          </CCButton>
          <CCButton
            onPress={handleSubmit}
            disabled={selectedSlots.length === 0}
            style={[
              styles.payNowButton,
              selectedSlots.length === 0 && styles.payNowButtonDisabled,
            ]}
            contentContainerStyle={styles.payNowButtonContent}>
            <CCText fontSize={FontSizes[16]} fontWeight="semibold" color={CCColors.TextColor.Dark}>
              {allowMultiple ? 'Save Selections' : 'Confirm'}
            </CCText>
          </CCButton>
        </Box>
      </Box>
    </SlideModal>
  );
};

const styles = StyleSheet.create({
  dateCard: {
    minWidth: 60,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: CCColors.White,
    borderWidth: 1,
    borderColor: CCColors.BorderBottom,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dateCardSelected: {
    backgroundColor: CCColors.TextColor.Dark,
    borderColor: CCColors.TextColor.Dark,
  },
  timeSlot: {
    minWidth: 60,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: CCColors.White,
    borderWidth: 1,
    borderColor: CCColors.BorderBottom,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  timeSlotSelected: {
    backgroundColor: CCColors.TextColor.Dark,
    borderColor: CCColors.TextColor.Dark,
  },
  payNowButton: {
    // backgroundColor: CCColors.LightYellow,
    borderRadius: 8,
    paddingVertical: 12,
  },
  payNowButtonDisabled: {
    opacity: 0.5,
  },
  payNowButtonContent: {
    padding: 0,
  },
  cancelButton: {
    flex: 1,
    backgroundColor: CCColors.Grey.Border,
    borderRadius: 8,
    paddingVertical: 12,
  },
  buttonContent: {
    padding: 0,
  },
});

export default DateTimeSlotModal;

